<template>
  <section class="leaderboard-page">
    <button type="button" class="leaderboard-back" @click="$emit('back')">
      <i class="el-icon-arrow-left" aria-hidden="true" />
      返回收益
    </button>

    <header class="leaderboard-head">
      <div>
        <h1>收益榜</h1>
        <p>{{ periodCaption }} · 匿名展示 · 每小时更新</p>
      </div>
      <div class="leaderboard-tabs" aria-label="收益榜周期">
        <button
          v-for="item in periods"
          :key="item.key"
          type="button"
          :class="{ active: period === item.key }"
          @click="$emit('period-change', item.key)"
        >
          {{ item.label }}
        </button>
      </div>
    </header>

    <div class="leaderboard-podium" aria-label="收益榜前三名">
      <article
        v-for="rank in podiumOrder"
        :key="rank"
        class="podium-card"
        :class="{ 'podium-card--first': rank === 1 }"
      >
        <img
          v-if="rank === 1"
          class="podium-card__crown"
          src="@/assets/rebuild/weekly-board-crown-gold.svg"
          alt=""
          aria-hidden="true"
        >
        <i class="podium-card__rank" :class="`rank-${rank}`">{{ rank }}</i>
        <strong class="podium-card__name">{{ podiumCard(rank).name }}</strong>
        <b class="podium-card__amount" :class="valueTone(podiumCard(rank).amountValue)">
          {{ podiumCard(rank).amount }}
        </b>
        <div class="podium-card__meta">
          <span>{{ podiumCard(rank).rate }}</span>
          <span>{{ podiumCard(rank).podiumBotText }}</span>
        </div>
      </article>
    </div>

    <section class="my-rank">
      <i>{{ myRankText }}</i>
      <div>
        <h2>我的当前排名 {{ myRankTitle }}</h2>
        <p v-if="isRanked">周期收益 {{ myAmount }} USDT · 收益率 {{ myRate }}</p>
      </div>
      <button type="button" @click="$emit('navigate', 'robots')">加仓提升排名</button>
    </section>

    <section class="leaderboard-table up-card">
      <div class="leaderboard-table__head">
        <span>排名</span>
        <span>用户</span>
        <span>运行策略</span>
        <span>收益率</span>
        <span>较上期</span>
        <span>周期收益 (USDT)</span>
      </div>

      <div class="leaderboard-table__body">
        <div
          v-for="row in tableRows"
          :key="`${row.rank}-${row.name}`"
          class="leaderboard-table__row"
          :class="{ mine: row.mine }"
        >
          <span><i class="row-rank" :class="`rank-${row.rank}`">{{ row.rank }}</i></span>
          <span class="row-user">
            <strong>{{ row.name }}</strong>
            <em v-if="row.mine">我</em>
          </span>
          <span>{{ row.botText }}</span>
          <span class="row-rate">{{ row.rate }}</span>
          <span><i class="movement" :class="movementClass(row.movement)">{{ movementText(row.movement) }}</i></span>
          <span class="row-result">
            <i><b :style="{ width: `${row.bar}%` }" /></i>
            <strong :class="valueTone(row.amountValue)">{{ row.amount }}</strong>
          </span>
        </div>
        <div v-if="!tableRows.length" class="leaderboard-empty">
          {{ loading ? '正在加载榜单…' : '暂无榜单数据' }}
        </div>
      </div>

      <footer>
        榜单基于已结算收益统计，用户信息匿名展示；仅展示前 20 名。
      </footer>
    </section>
  </section>
</template>

<script>
import {
  finiteNumber,
  firstNumber,
  formatNumber,
  formatSigned,
  mapLeader
} from '@/utils/portal-data'

const PERIODS = [
  { key: 'week', label: '本周', dateType: 1 },
  { key: 'month', label: '本月', dateType: 2 },
  { key: 'all', label: '总榜', dateType: 0 }
]

function dateLabel(date) {
  return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function robotCount(raw) {
  return firstNumber(raw || {}, [
    'robot_count',
    'robotCount',
    'robot_quantity',
    'strategy_count',
    'strategyCount',
    'strategy_num',
    'run_count'
  ])
}

export default {
  name: 'EarningsLeaderboardPanel',
  props: {
    leaders: { type: Array, default: () => [] },
    rank: { type: Object, default: null },
    userInfo: { type: Object, default: () => ({}) },
    period: { type: String, default: 'week' },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      periods: PERIODS,
      podiumOrder: [2, 1, 3]
    }
  },
  computed: {
    periodCaption() {
      const today = new Date()
      if (this.period === 'all') return '全部周期'
      const start = new Date(today)
      if (this.period === 'month') {
        start.setDate(1)
        return `${dateLabel(start)} ~ ${dateLabel(today)}`
      }
      const day = today.getDay() || 7
      start.setDate(today.getDate() - day + 1)
      return `${dateLabel(start)} ~ ${dateLabel(today)}`
    },
    rows() {
      const mapped = this.leaders
        .slice(0, 20)
        .map((item, index) => mapLeader(item, index, this.userInfo))
      const max = mapped.reduce((result, row) => {
        const amount = finiteNumber(row.amountValue)
        return amount === null ? result : Math.max(result, Math.abs(amount))
      }, 0)
      return mapped.map(row => {
        const count = robotCount(row.raw)
        const amount = finiteNumber(row.amountValue)
        return {
          ...row,
          botText: count === null ? '--' : `${formatNumber(count, 0)} 台`,
          podiumBotText: count === null ? '--' : `${formatNumber(count, 0)} 台策略`,
          movement: firstNumber(row.raw, ['rank_change', 'rankChange', 'change_rank', 'change']),
          bar: amount === null || !max ? 0 : Math.max(8, Math.abs(amount) / max * 100)
        }
      })
    },
    tableRows() {
      return this.rows.filter(row => Number(row.rank) > 3)
    },
    rankRow() {
      if (!this.rank || typeof this.rank !== 'object') return null
      return mapLeader(this.rank, Math.max(this.rows.length, 0), this.userInfo)
    },
    mineRow() {
      return this.rows.find(row => row.mine) || this.rankRow
    },
    myRankValue() {
      const value = firstNumber(this.rank || {}, ['rankNum', 'rank'])
      if (value !== null) return value
      return this.mineRow ? finiteNumber(this.mineRow.rank) : null
    },
    isRanked() {
      return this.myRankValue !== null && this.myRankValue > 0 && this.myRankValue <= 450
    },
    myRankText() {
      return this.isRanked ? formatNumber(this.myRankValue, 0) : '--'
    },
    myRankTitle() {
      return this.isRanked ? `第 ${formatNumber(this.myRankValue, 0)} 名` : '未入榜'
    },
    myAmount() {
      return this.mineRow ? formatSigned(this.mineRow.amountValue) : '--'
    },
    myRate() {
      return this.mineRow ? this.mineRow.rate : '--'
    }
  },
  methods: {
    podiumCard(rank) {
      return this.rows.find(row => Number(row.rank) === rank) || {
        name: '暂无用户',
        amountValue: null,
        amount: '--',
        rate: '--',
        botText: '--',
        podiumBotText: '--'
      }
    },
    valueTone(value) {
      const number = finiteNumber(value)
      if (number === null || number === 0) return 'is-neutral'
      return number > 0 ? 'is-positive' : 'is-negative'
    },
    movementClass(value) {
      if (value > 0) return 'movement-up'
      if (value < 0) return 'movement-down'
      return 'movement-flat'
    },
    movementText(value) {
      if (value > 0) return `▲ ${formatNumber(Math.abs(value), 0)}`
      if (value < 0) return `▼ ${formatNumber(Math.abs(value), 0)}`
      return '—'
    }
  }
}
</script>

<style scoped>
.leaderboard-page {
  color: #1b1f2a;
  animation: page-in .25s ease;
}

.leaderboard-back {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 14px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1f6bff;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
}

.leaderboard-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.leaderboard-head h1 {
  margin: 0;
  color: #171b25;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: .2px;
}

.leaderboard-head p {
  margin: 5px 0 0;
  color: #8a90a0;
  font-size: 13px;
}

.leaderboard-tabs {
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 9px;
  background: #f5f6f9;
}

.leaderboard-tabs button {
  padding: 7px 18px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #697186;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.leaderboard-tabs button.active {
  background: #fff;
  color: #1f6bff;
  box-shadow: 0 1px 4px rgba(37, 54, 89, .1);
}

.leaderboard-podium {
  display: grid;
  grid-template-columns: 1fr 1.15fr 1fr;
  align-items: end;
  gap: 20px;
  margin-bottom: 20px;
}

.podium-card {
  display: flex;
  min-height: 188px;
  box-sizing: border-box;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  padding: 22px;
  border: 1px solid #e7eaf1;
  border-radius: 14px;
  background: #fff;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, .04),
    0 6px 20px rgba(16, 24, 40, .04);
}

.podium-card--first {
  position: relative;
  min-height: 215px;
  gap: 9px;
  padding: 28px 22px 24px;
  border-color: #f0dca0;
  background: linear-gradient(180deg, #fffbef, #fff 62%);
  box-shadow: 0 10px 26px rgba(176, 138, 32, .12);
}

.podium-card__crown {
  position: absolute;
  top: -13px;
  left: 50%;
  width: 26px;
  height: 26px;
  transform: translateX(-50%);
}

.podium-card__rank,
.row-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f0f2f6;
  color: #637087;
  font-style: normal;
  font-weight: 800;
}

.podium-card__rank {
  width: 44px;
  height: 44px;
  font-size: 18px;
  font-variant-numeric: tabular-nums;
}

.podium-card--first .podium-card__rank {
  width: 52px;
  height: 52px;
  font-size: 21px;
}

.podium-card__rank.rank-1,
.row-rank.rank-1 {
  background: #fdf0d0;
  color: #b7791f;
}

.podium-card__rank.rank-2,
.row-rank.rank-2 {
  background: #edf0f6;
  color: #5a6478;
}

.podium-card__rank.rank-3,
.row-rank.rank-3 {
  background: #fbe8da;
  color: #b4560b;
}

.podium-card__name {
  color: #1b1f2a;
  font-size: 15px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.podium-card--first .podium-card__name {
  font-size: 16px;
}

.podium-card__amount {
  color: #0b9b57;
  font-size: 22px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.podium-card--first .podium-card__amount {
  font-size: 27px;
  letter-spacing: -.3px;
}

.podium-card__meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.podium-card__meta span {
  padding: 3px 10px;
  border-radius: 999px;
  background: #f4f5f9;
  color: #5a6072;
  font-size: 12px;
  font-weight: 700;
}

.podium-card__meta span:first-child {
  background: #e8f6ee;
  color: #0b7a47;
  font-variant-numeric: tabular-nums;
}

.my-rank {
  display: flex;
  min-height: 66px;
  box-sizing: border-box;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  padding: 14px 20px;
  border: 1px solid #dce8fd;
  border-radius: 14px;
  background: linear-gradient(90deg, #eff5fe, #fff);
}

.my-rank > i {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #e4eefe;
  color: #1f6bff;
  font-style: normal;
  font-weight: 800;
}

.my-rank h2 {
  margin: 0;
  color: #2b3343;
  font-size: 14px;
  font-weight: 800;
}

.my-rank > div {
  display: flex;
  align-items: center;
  gap: 14px;
}

.my-rank p {
  margin: 0;
  color: #8a90a0;
  font-size: 13px;
}

.my-rank button {
  margin-left: auto;
  padding: 9px 18px;
  border: 0;
  border-radius: 8px;
  background: #1f6bff;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.leaderboard-table {
  overflow: hidden;
  border-radius: 14px;
}

.leaderboard-table__head,
.leaderboard-table__row {
  display: grid;
  grid-template-columns: .5fr 1.5fr .9fr .9fr .7fr 2.2fr;
  align-items: center;
  gap: 10px;
}

.leaderboard-table__head {
  padding: 14px 24px 8px;
  border-bottom: 1px solid #f0f2f6;
  color: #8a90a0;
  font-size: 12.5px;
  font-weight: 600;
}

.leaderboard-table__head span:last-child {
  text-align: right;
}

.leaderboard-table__row {
  min-height: 60px;
  padding: 12px 24px;
  box-sizing: border-box;
  border-bottom: 1px solid #f6f7fa;
  color: #586176;
  font-size: 14px;
  transition: background .16s ease;
}

.leaderboard-table__row:hover {
  background: #fafbfe;
}

.leaderboard-table__row.mine {
  background: #f2f6fd;
}

.row-rank {
  width: 28px;
  height: 28px;
  font-size: 12.5px;
}

.row-user {
  display: flex;
  align-items: center;
  gap: 9px;
}

.row-user strong {
  color: #303746;
  font-weight: 700;
}

.row-user em {
  padding: 2px 7px;
  border-radius: 999px;
  background: #1f6bff;
  color: #fff;
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
}

.row-rate {
  color: #009b59;
  font-weight: 700;
}

.movement {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
}

.movement-up {
  background: #e8f7ef;
  color: #008a4f;
}

.movement-down {
  background: #feeeee;
  color: #df4048;
}

.movement-flat {
  background: #f3f5f8;
  color: #8b93a4;
}

.row-result {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.row-result > i {
  width: 100%;
  max-width: 220px;
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf0f5;
}

.row-result > i b {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #1f6bff;
}

.row-result strong {
  min-width: 96px;
  text-align: right;
  color: #009b59;
  font-weight: 800;
}

.leaderboard-empty {
  display: flex;
  min-height: 120px;
  align-items: center;
  justify-content: center;
  color: #a0a8b8;
  font-size: 13px;
}

.leaderboard-table footer {
  padding: 14px 24px;
  color: #98a0af;
  font-size: 12.5px;
}

.is-positive {
  color: #009b59 !important;
}

.is-negative {
  color: #e34b55 !important;
}

.is-neutral {
  color: #1b1f2a !important;
}

@keyframes page-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1100px) {
  .leaderboard-podium {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .leaderboard-table__head,
  .leaderboard-table__row {
    grid-template-columns: 70px 1.4fr .8fr .8fr .7fr 1.6fr;
  }
}
</style>
